import cn from 'classnames';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import formatCurrency from 'core-lib/utils/currency';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import { useEffect, useMemo, useRef } from 'react';
import { ProductCardProp } from 'src/modules/parkplusStoreV2/types/product';
import { Typography } from 'ui-v2/components/Typography';

import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import { trackProductViewedStoreV2 } from 'src/modules/parkplusStoreV2/utils/events';
import { RatingTag } from 'ui-v2/components/RatingTag';
import AddToCart from '../../../AddToCart';
import s from './StoreProductCard.module.scss';

export const StoreProductCard = (productProp: ProductCardProp) => {
  const rootRef = useRef();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  const { product, id, position, section } = productProp;
  const isOutOfStock = useMemo(() => product.stock === 0, []);

  useEffect(() => {
    if (inViewPort && !isEventFired.current) {
      trackProductViewedStoreV2(section, product?.productId, {
        description: product?.eventType,
      });
      isEventFired.current = true;
    }
  }, [section, inViewPort, product.productId, product.eventType]);

  return (
    <div
      className={cn(s.root, {
        [s.outOfStock]: isOutOfStock,
      })}
      id={id}
      data-index={position}
      ref={rootRef}
    >
      <div className={s.imageContainer}>
        <AnimatedImage src={product?.coverImage} className={s.coverImage} />
        {product?.tag && (
          <div className={s.tag}>
            <div className={s.shape}></div>
            <Typography
              label={product?.priceNudge ? product?.priceNudge : product?.tag}
              color="on_dark_bg"
              variant="tiny_bold"
              style={{ lineHeight: '12px' }}
              lineClamp={1}
            />
          </div>
        )}
        {product?.rating ? (
          <div className={s.rating}>
            <RatingTag size="s" rating={+product?.rating} />
          </div>
        ) : null}
        {isOutOfStock && (
          <div className={s.soldout}>
            <Typography
              label="Sold out"
              variant="body_3_medium"
              color="white_alpha_tertiary"
            />
          </div>
        )}
      </div>
      <div className={s.bottom}>
        <Typography
          label={product.title}
          variant="label_3_medium"
          className={s.title}
        />
        <div className={s.money}>
          <Typography
            label={formatCurrency(product.loyaltyPrice)}
            variant="label_3_bold"
          />
          {product.loyaltyPrice !== product.price && <Typography
            label={formatCurrency(product?.price)}
            variant="label_3_regular"
            className={s.lineThrough}
            color="tertiary"
          />}
        </div>
        {product?.loyaltyPoints > 0 && product?.loyaltyPrice >= 0 ? (
          <div className={s.mlPrice}>
            <Typography label={`using `} color="offer" variant="label_4_bold" />
            <AnimatedImage
              src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240104160730062768.png"
              className={s.drop}
              alt="loyalty drop"
            />
            <Typography
              label={formatFuelBalance(product?.loyaltyPoints)}
              color="offer"
              variant="label_4_bold"
            />
          </div>
        ) : null}
      </div>
      <div className={s.addToCart}>
        <AddToCart product={product} section={section} />
      </div>
    </div>
  );
};
